import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { studentApi, therapistApi, documentTransmittalsApi, pendingReportsApi } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import OverdueFeesWidget from '../components/Dashboard/OverdueFeesWidget';
import './Dashboard.css';

interface DashboardStats {
  totalStudents: number;
  activeStudents: number;
  totalTherapists: number;
  activeTherapists: number;
  totalDocuments: number;
  pendingReports: number;
  documentsThisWeek: number;
  emailsSent: number;
}

interface RecentActivity {
  id: number;
  type: 'document' | 'schedule' | 'student' | 'therapist';
  description: string;
  timestamp: string;
  status?: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalStudents: 0,
    activeStudents: 0,
    totalTherapists: 0,
    activeTherapists: 0,
    totalDocuments: 0,
    pendingReports: 0,
    documentsThisWeek: 0,
    emailsSent: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const { canAccessModule } = usePermissions();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Only fetch data for modules the user has access to
      const promises: { key: string; promise: Promise<any> }[] = [];

      if (canAccessModule('students')) {
        promises.push({ key: 'students', promise: studentApi.getAll({ limit: 1000 }) });
      }
      if (canAccessModule('therapists')) {
        promises.push({ key: 'therapists', promise: therapistApi.getAll({ limit: 1000 }) });
      }
      if (canAccessModule('document-transmittals')) {
        promises.push({ key: 'documents', promise: documentTransmittalsApi.getAll() });
      }
      if (canAccessModule('pending-reports')) {
        promises.push({ key: 'pendingReports', promise: pendingReportsApi.getStats() });
      }

      const results = await Promise.all(promises.map(p => p.promise.catch(() => null)));

      const responseMap: Record<string, any> = {};
      promises.forEach((p, i) => {
        responseMap[p.key] = results[i];
      });

      const students = responseMap.students?.data?.students || [];
      const therapists = responseMap.therapists?.data?.therapists || [];
      const documents = responseMap.documents?.data?.documents || [];

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const documentsThisWeek = documents.filter((doc: any) =>
        new Date(doc.created_at) >= oneWeekAgo
      ).length;

      const emailsSent = documents.filter((doc: any) =>
        doc.status === 'Email Sent'
      ).length;

      setStats({
        totalStudents: students.length,
        activeStudents: students.filter((s: any) => s.is_active).length,
        totalTherapists: therapists.length,
        activeTherapists: therapists.filter((t: any) => t.is_active).length,
        totalDocuments: documents.length,
        pendingReports: responseMap.pendingReports?.data?.total || 0,
        documentsThisWeek,
        emailsSent
      });

      // Generate recent activity from documents
      const recentDocs = documents
        .slice(0, 5)
        .map((doc: any) => ({
          id: doc.id,
          type: 'document' as const,
          description: `${doc.document_type} uploaded for ${doc.student_name}`,
          timestamp: doc.created_at,
          status: doc.status
        }));

      setRecentActivity(recentDocs);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [canAccessModule]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'document': return '📄';
      case 'schedule': return '📅';
      case 'student': return '👨‍🎓';
      case 'therapist': return '👨‍⚕️';
      default: return '📋';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Email Sent': return 'status-success';
      case 'Processing': return 'status-warning';
      case 'Email Failed': return 'status-error';
      default: return 'status-default';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard Overview</h2>
        <p>Welcome to your therapy management system</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        {canAccessModule('students') && (
          <div className="stat-card primary">
            <div className="stat-icon">👨‍🎓</div>
            <div className="stat-content">
              <div className="stat-number">{stats.activeStudents}</div>
              <div className="stat-label">Active Students</div>
              <div className="stat-detail">of {stats.totalStudents} total</div>
            </div>
            <Link to="/students" className="stat-link">View All →</Link>
          </div>
        )}

        {canAccessModule('therapists') && (
          <div className="stat-card secondary">
            <div className="stat-icon">👨‍⚕️</div>
            <div className="stat-content">
              <div className="stat-number">{stats.activeTherapists}</div>
              <div className="stat-label">Active Therapists</div>
              <div className="stat-detail">of {stats.totalTherapists} total</div>
            </div>
            <Link to="/therapists" className="stat-link">View All →</Link>
          </div>
        )}

        {canAccessModule('pending-reports') && (
          <div className="stat-card warning">
            <div className="stat-icon">⚠️</div>
            <div className="stat-content">
              <div className="stat-number">{stats.pendingReports}</div>
              <div className="stat-label">Pending Reports</div>
              <div className="stat-detail">require attention</div>
            </div>
            <Link to="/pending-reports" className="stat-link">View All →</Link>
          </div>
        )}

        {canAccessModule('document-transmittals') && (
          <>
            <div className="stat-card success">
              <div className="stat-icon">📄</div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalDocuments}</div>
                <div className="stat-label">Total Documents</div>
                <div className="stat-detail">{stats.documentsThisWeek} this week</div>
              </div>
              <Link to="/document-transmittals" className="stat-link">View All →</Link>
            </div>

            <div className="stat-card info">
              <div className="stat-icon">📧</div>
              <div className="stat-content">
                <div className="stat-number">{stats.emailsSent}</div>
                <div className="stat-label">Emails Sent</div>
                <div className="stat-detail">successfully delivered</div>
              </div>
              <Link to="/document-transmittals" className="stat-link">View All →</Link>
            </div>
          </>
        )}

        {canAccessModule('schedules') && (
          <div className="stat-card accent">
            <div className="stat-icon">📅</div>
            <div className="stat-content">
              <div className="stat-number">Active</div>
              <div className="stat-label">Schedule System</div>
              <div className="stat-detail">ready for use</div>
            </div>
            <Link to="/schedules" className="stat-link">View All →</Link>
          </div>
        )}
      </div>

      {/* Dashboard Content Grid */}
      <div className="dashboard-content">
        {/* Overdue Fees Widget - only visible to users with fees permission */}
        {canAccessModule('fees') && (
          <div className="dashboard-widget">
            <OverdueFeesWidget />
          </div>
        )}

        {/* Recent Activity */}
        {canAccessModule('document-transmittals') && (
          <div className="dashboard-section">
            <div className="section-header">
              <h3>Recent Activity</h3>
              <button onClick={fetchDashboardData} className="refresh-btn">
                🔄 Refresh
              </button>
            </div>

            <div className="activity-list">
              {recentActivity.length === 0 ? (
                <div className="no-activity">
                  <p>No recent activity to display</p>
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-icon">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="activity-content">
                      <div className="activity-description">{activity.description}</div>
                      <div className="activity-timestamp">{formatDate(activity.timestamp)}</div>
                    </div>
                    {activity.status && (
                      <div className={`activity-status ${getStatusColor(activity.status)}`}>
                        {activity.status}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="dashboard-section">
          <div className="section-header">
            <h3>Quick Actions</h3>
          </div>

          <div className="quick-actions">
            {canAccessModule('document-transmittals') && (
              <Link to="/document-transmittals" className="action-card">
                <div className="action-icon">📄</div>
                <div className="action-title">Upload Document</div>
                <div className="action-description">Add new student documents</div>
              </Link>
            )}

            {canAccessModule('schedules') && (
              <Link to="/schedules" className="action-card">
                <div className="action-icon">📅</div>
                <div className="action-title">Create Schedule</div>
                <div className="action-description">Plan therapy sessions</div>
              </Link>
            )}

            {canAccessModule('students') && (
              <Link to="/students" className="action-card">
                <div className="action-icon">👨‍🎓</div>
                <div className="action-title">Add Student</div>
                <div className="action-description">Register new student</div>
              </Link>
            )}

            {canAccessModule('therapists') && (
              <Link to="/therapists" className="action-card">
                <div className="action-icon">👨‍⚕️</div>
                <div className="action-title">Add Therapist</div>
                <div className="action-description">Register new therapist</div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
